import { useTu } from "@src/appV2/i18n/useTu";
import { Box, InputAdornment, Stack, TextField } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/components/Text";
import clamp from "lodash/clamp";
import { useEffect, useRef, useState } from "react";

import { majorToMinor, minorToMajor } from "../../ShiftDetails/components/chargeRateUtils";

const BONUS_INCREMENT_IN_MINOR_UNITS = 100;

interface InviteHourlyBonusStepperProps {
  boostInMinorUnits: number;
  maxBoostInMinorUnits: number;
  onBoostChange: (boostInMinorUnits: number) => void;
}

const MIN_BOOST_IN_MINOR_UNITS = 0;

/** Allows empty, numbers, and numbers with up to 2 decimal places */
function isValidCurrencyInput(value: string): boolean {
  if (value === "" || value === ".") {
    return true;
  }
  return /^\d*\.?\d{0,2}$/.test(value);
}

export function InviteHourlyBonusStepper(props: InviteHourlyBonusStepperProps) {
  const { tu } = useTu("schedule");
  const { boostInMinorUnits, maxBoostInMinorUnits, onBoostChange } = props;

  const isAtMin = boostInMinorUnits <= MIN_BOOST_IN_MINOR_UNITS;
  const isAtMax = boostInMinorUnits >= maxBoostInMinorUnits;

  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(() => minorToMajor(boostInMinorUnits).toFixed(2));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      setInputValue(minorToMajor(boostInMinorUnits).toFixed(2));
    }
  }, [boostInMinorUnits, isFocused]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (!isValidCurrencyInput(value)) {
      return;
    }

    setInputValue(value);

    const numericValue = Number.parseFloat(value);
    if (!Number.isNaN(numericValue)) {
      onBoostChange(
        clamp(majorToMinor(numericValue), MIN_BOOST_IN_MINOR_UNITS, maxBoostInMinorUnits)
      );
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);

    const numericValue = Number.parseFloat(inputValue);
    if (Number.isNaN(numericValue)) {
      setInputValue(minorToMajor(boostInMinorUnits).toFixed(2));
      return;
    }

    const clampedInMinorUnits = clamp(
      majorToMinor(numericValue),
      MIN_BOOST_IN_MINOR_UNITS,
      maxBoostInMinorUnits
    );
    setInputValue(minorToMajor(clampedInMinorUnits).toFixed(2));
    onBoostChange(clampedInMinorUnits);
  };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: `1px solid ${theme.palette.border?.base}`,
        borderRadius: theme.borderRadius?.large,
        paddingLeft: 2,
        minWidth: 10,
      })}
    >
      <TextField
        inputRef={inputRef}
        size="small"
        value={inputValue}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          startAdornment: (
            <InputAdornment disableTypography position="start">
              <Text
                inline
                medium
                variant="caption"
                sx={(theme) => ({ color: theme.palette.base?.strong })}
              >
                $
              </Text>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment disableTypography position="end">
              <Text
                inline
                medium
                variant="caption"
                sx={(theme) => ({ color: theme.palette.base?.strong })}
              >
                /hr
              </Text>
            </InputAdornment>
          ),
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props -- inputProps is for HTML attrs, InputProps is for MUI component props
        inputProps={{
          "aria-label": tu("Hourly bonus"),
          inputMode: "decimal",
          style: { textAlign: "center", width: "3.5ch", padding: 0 },
        }}
        sx={(theme) => ({
          "& .MuiInputBase-input": {
            fontWeight: theme.typography?.fontWeightMedium,
            fontSize: theme.typography?.caption?.fontSize,
            color: theme.palette.base?.strong,
          },
        })}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onFocus={() => {
          setIsFocused(true);
        }}
      />

      <Stack sx={{ gap: 0 }}>
        <IconButton
          aria-label={tu("Increase hourly bonus")}
          disabled={isAtMax}
          iconSize="small"
          iconType="chevron-up"
          size="small"
          sx={{ width: "1.125rem", height: "1.125rem", minWidth: "1.125rem", padding: 0 }}
          variant="muted"
          onClick={() => {
            onBoostChange(
              clamp(
                boostInMinorUnits + BONUS_INCREMENT_IN_MINOR_UNITS,
                MIN_BOOST_IN_MINOR_UNITS,
                maxBoostInMinorUnits
              )
            );
          }}
        />
        <IconButton
          aria-label={tu("Decrease hourly bonus")}
          disabled={isAtMin}
          iconSize="small"
          iconType="chevron-down"
          size="small"
          sx={{ width: "1.125rem", height: "1.125rem", minWidth: "1.125rem", padding: 0 }}
          variant="muted"
          onClick={() => {
            onBoostChange(
              clamp(
                boostInMinorUnits - BONUS_INCREMENT_IN_MINOR_UNITS,
                MIN_BOOST_IN_MINOR_UNITS,
                maxBoostInMinorUnits
              )
            );
          }}
        />
      </Stack>
    </Box>
  );
}
